import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Doughnut } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip as ChartTooltip, Legend } from 'chart.js';
import {
  TABLES,
  airtableUrl,
  AIRTABLE_HEADERS,
  fetchWithRetry,
  fetchAllAirtableData,
  showToast,
  formatDate,
  normalizeEmail,
  isProblematicEmail,
  parseVigenciaDate,
  getInitials,
} from './lib';
import {
  FullScreenSpinner,
  SkeletonCard,
  SkeletonForm,
  SkeletonTable,
  SkeletonStats,
  ConfirmDialog,
  ResultDialog,
  useDebounce,
} from './ui';
import {
  computeEmployeeBalances,
  filterApprovedRequestsForEmployee,
  sumRemainingBalances,
} from './domain/balances';
import {
  buildRequestFields,
  canCancelRequest,
  computeDayRange,
  countRequestStatuses,
  evaluateRequestRules,
  filterAndSortBossRequests,
  filterPastRequests,
  filterRequestsForBoss,
  pageRecords,
  resolveBossField,
  splitWcDateRanges,
} from './domain/requests';
import './Dashboard.css';

Chart.register(ArcElement, ChartTooltip, Legend);

const BOSS_PAGE_SIZE = 25;

const Dashboard = () => {
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  const [role, setRole] = useState('');
  const [roleLoaded, setRoleLoaded] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const [airtableData, setAirtableData] = useState([]);
  const [bosses, setBosses] = useState([]);
  const [userData, setUserData] = useState(null);
  const [pastRequests, setPastRequests] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 250);
  const [filterType, setFilterType] = useState('todos');
  const [sortBy, setSortBy] = useState('jerarquico');
  const [sortOrder, setSortOrder] = useState('asc');
  const [dateRangeFilter, setDateRangeFilter] = useState({
    startDate: '',
    endDate: '',
  });
  const [expandedFilters, setExpandedFilters] = useState(false);

  const [formData, setFormData] = useState({
    employeeNumber: '',
    email: '',
    startDate: '',
    endDate: '',
    permissionType: 'vacaciones',
    boss: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [resultModal, setResultModal] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const showResult = (variant, title, message) => {
    showToast(variant, title, message);
    if (mountedRef.current) setResultModal({ variant, title, message });
  };

  // Vuelve a cargar los datos desde Airtable para reflejar los cambios en la interfaz.
  const refreshData = () => setRefreshKey((k) => k + 1);

  const [loadingRowIds, setLoadingRowIds] = useState(new Set());
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [pastFilter, setPastFilter] = useState('todas');
  const [bossPage, setBossPage] = useState(1);
  const [logoutConfirm, setLogoutConfirm] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetchWithRetry('/api/dashboard', {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          console.error('Failed to fetch dashboard data. Redirecting to login.');
          navigate('/login');
          return;
        }

        const data = await response.json();

        if (!mountedRef.current) return;

        setRole(data.role);
        setUserEmail(data.email || '');
        setRoleLoaded(true);

        const loggedInEmail = (data.email || '').trim().toLowerCase();

        const employeesUrl = airtableUrl(TABLES.employees);
        const requestsUrl = airtableUrl(TABLES.requests);

        let allRecords;
        let adminRecords;
        let bossTableRecords = [];

        if (data.role === 'employee') {
          // Quita comillas simples para no romper la fórmula (los correos/IDs no las tienen).
          const escapeFormula = (v) => String(v).replace(/'/g, '');
          const emailFormula = `LOWER(TRIM({EMAIL})) = '${escapeFormula(loggedInEmail)}'`;

          // Solo el registro del empleado + la lista de jefes (tabla chica), en paralelo.
          [allRecords, bossTableRecords] = await Promise.all([
            fetchAllAirtableData(employeesUrl, { filterByFormula: emailFormula }),
            fetchAllAirtableData(airtableUrl(TABLES.bosses)),
          ]);

          // Muchas solicitudes usan un correo personal distinto al de login: el
          // match real es por ID_Empleado, así que la fórmula combina email O ID.
          const myEmployeeId =
            allRecords.length > 0 ? allRecords[0].fields.ID_Empleado : null;

          const requestClauses = [`LOWER(TRIM({EMAIL})) = '${escapeFormula(loggedInEmail)}'`];
          if (myEmployeeId !== null && myEmployeeId !== undefined && String(myEmployeeId) !== '') {
            requestClauses.push(`({ID_Empleado} & '') = '${escapeFormula(String(myEmployeeId))}'`);
          }
          const requestsFormula =
            requestClauses.length > 1 ? `OR(${requestClauses.join(', ')})` : requestClauses[0];

          adminRecords = await fetchAllAirtableData(requestsUrl, {
            filterByFormula: requestsFormula,
          });
        } else {
          allRecords = await fetchAllAirtableData(requestsUrl);
          adminRecords = allRecords;
        }

        if (!mountedRef.current) return;

        const filteredRecords =
          data.role === 'boss' ? filterRequestsForBoss(allRecords, loggedInEmail) : allRecords;

        console.log('Filtered Records for Boss after adjustment:', filteredRecords);
        setAirtableData(filteredRecords);

        const userRecords = allRecords.filter((record) => {
          const recordEmail = normalizeEmail(record.fields.EMAIL);
          const normalizedLoggedInEmail = normalizeEmail(loggedInEmail);
          return recordEmail === normalizedLoggedInEmail;
        });

        console.log('Registros de usuario filtrados por email:', userRecords);

        const problematicEmails = allRecords.filter((record) => {
          const email = record.fields.EMAIL;
          return isProblematicEmail(email);
        });

        if (problematicEmails.length > 0) {
          console.warn(
            `⚠️ ADVERTENCIA: Se encontraron ${problematicEmails.length} empleados con emails problemáticos`
          );
          problematicEmails.forEach((emp) => {
            console.warn(`  - ID: ${emp.fields.ID_Empleado}, Email: "${emp.fields.EMAIL}"`);
          });
        }

        const loggedInEmployeeId =
          userRecords.length > 0 ? userRecords[0].fields.ID_Empleado : null;

        if (data.role === 'employee') {
          console.log(`🔍 DIAGNÓSTICO fetchPastRequests para empleado ${loggedInEmployeeId}:`);
          console.log(`  Email logueado: "${loggedInEmail}"`);
          console.log(`  Email normalizado: "${normalizeEmail(loggedInEmail)}"`);
          console.log(`  Total registros en BD: ${adminRecords.length}`);

          const pastFiltered = adminRecords.filter((record) => {
            const recordEmail = normalizeEmail(record.fields.EMAIL);
            const recordEmployeeId = record.fields.ID_Empleado;
            const normalizedLoggedInEmail = normalizeEmail(loggedInEmail);
            const emailMatch = recordEmail === normalizedLoggedInEmail;
            const idMatch = String(recordEmployeeId) === String(loggedInEmployeeId);

            if (emailMatch || idMatch) {
              console.log(
                `  ✅ Solicitud encontrada: ID=${recordEmployeeId}, Email="${record.fields.EMAIL}"`
              );
            }
            return emailMatch || idMatch;
          });

          console.log(
            `📊 Solicitudes encontradas para empleado ${loggedInEmployeeId}: ${pastFiltered.length}`
          );
          if (!mountedRef.current) return;
          setPastRequests(pastFiltered);
        }

        const approvedRequests = filterApprovedRequestsForEmployee(
          adminRecords,
          loggedInEmail,
          loggedInEmployeeId
        );

        console.log('=== SOLICITUDES APROBADAS ===');
        approvedRequests.forEach((request, index) => {
          console.log(`Solicitud #${index + 1}:`);
          console.log('ID:', request.id);
          console.log('Campos disponibles:', Object.keys(request.fields));
          console.log('FECHA_INICIO:', request.fields.FECHA_INICIO);
          console.log('FECHA_FIN:', request.fields.FECHA_FIN);
          console.log('LastModified (si existe):', request.fields.LastModified);

          for (const key in request.fields) {
            if (
              key.toLowerCase().includes('modif') ||
              key.toLowerCase().includes('date') ||
              key.toLowerCase().includes('time') ||
              key.toLowerCase().includes('fecha')
            ) {
              console.log(`Campo posible: ${key} =`, request.fields[key]);
            }
          }

          if (request.createdTime) {
            console.log('createdTime:', request.createdTime);
          }

          if (request.lastModifiedTime) {
            console.log('lastModifiedTime:', request.lastModifiedTime);
          }

          console.log('----------------------------');
        });

        const currentDate = new Date();
        console.log(`Fecha actual: ${currentDate.toISOString()}`);
        const processedRecords = computeEmployeeBalances(
          userRecords,
          approvedRequests,
          currentDate
        );

        if (!mountedRef.current) return;
        setUserData(processedRecords);

        if (data.role === 'employee') {
          setBosses(
            Array.from(
              new Set(
                bossTableRecords
                  .map((record) => {
                    const assignee = record.fields.Assignee;
                    const name = (assignee && assignee.name) || record.fields.EMAIL;
                    const email = (assignee && assignee.email) || record.fields.EMAIL;
                    if (!email) return null;
                    return JSON.stringify({ name, email });
                  })
                  .filter(Boolean)
              )
            ).map((bossStr) => JSON.parse(bossStr))
          );
        }

        if (!mountedRef.current) return;
        setDataLoaded(true);
      } catch (error) {
        console.error('Error en la autenticación:', error);
        if (!mountedRef.current) return;
        showToast(
          'danger',
          'Error al cargar datos',
          error.message || 'No se pudo conectar. Intenta recargar la página.'
        );
        setDataLoaded(true);
      }
    };

    loadData();
  }, [navigate, refreshKey]);

  useEffect(() => {
    if (userData && userData.length > 0) {
      const first = userData[0];
      setFormData((prev) => ({
        ...prev,
        employeeNumber: first.fields?.ID_Empleado != null ? String(first.fields.ID_Empleado) : prev.employeeNumber,
        email: first.fields?.EMAIL || prev.email,
      }));
    }
  }, [userData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'email') {
      const normalizedEmail = normalizeEmail(value);
      if (value && isProblematicEmail(value)) {
        console.warn('Email problemático detectado:', value, 'Normalizado:', normalizedEmail);
      }
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleLogout = async () => {
    try {
      const response = await fetchWithRetry('/logout', {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        window.location.href = '/login';
      } else {
        showToast('danger', 'Error', 'No se pudo cerrar sesión.');
      }
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      showToast('danger', 'Error', 'Error de conexión al cerrar sesión.');
    }
  };

  const openApprovalConfirm = (recordId, status, record) => {
    const employeeId = record.fields?.ID_Empleado || 'N/A';
    const tipo = record.fields?.TIPO_DE_PERMISO || 'permiso';
    const inicio = record.fields?.FECHA_INICIO ? formatDate(record.fields.FECHA_INICIO) : 'N/A';
    const fin = record.fields?.FECHA_FIN ? formatDate(record.fields.FECHA_FIN) : 'N/A';

    setConfirmAction({
      recordId,
      status,
      title: status === 'APROBADA' ? 'Aprobar solicitud' : 'Rechazar solicitud',
      variant: status === 'APROBADA' ? 'success' : 'danger',
      icon: status === 'APROBADA' ? 'bi-check-circle-fill' : 'bi-x-circle-fill',
      confirmText: status === 'APROBADA' ? 'Sí, aprobar' : 'Sí, rechazar',
      message: `¿Confirmas ${status === 'APROBADA' ? 'aprobar' : 'rechazar'} la solicitud de ${tipo} del empleado ${employeeId} del ${inicio} al ${fin}? Se notificará al empleado por correo.`,
    });
  };

  const handleApprovalConfirmed = async () => {
    if (!confirmAction) return;
    const { recordId, status } = confirmAction;

    setConfirmLoading(true);

    const prevData = airtableData;
    const updatedData = airtableData.map((r) =>
      r.id === recordId ? { ...r, fields: { ...r.fields, APROBACION: status } } : r
    );
    setAirtableData(updatedData);

    setLoadingRowIds((prev) => {
      const s = new Set(prev);
      s.add(recordId);
      return s;
    });

    setConfirmAction(null);
    setConfirmLoading(false);

    try {
      const response = await fetchWithRetry(airtableUrl(TABLES.requests), {
        method: 'PATCH',
        headers: {
          ...AIRTABLE_HEADERS,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          records: [
            {
              id: recordId,
              fields: {
                APROBACION: status,
              },
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error('Error al actualizar la aprobación');
      }

      const recordResponse = await fetchWithRetry(
        `${airtableUrl(TABLES.requests)}/${recordId}`,
        {
          headers: AIRTABLE_HEADERS,
        }
      );

      const recordData = await recordResponse.json();
      const record = recordData.fields;

      const subject = `Tu solicitud ha sido ${status.toLowerCase()}`;
      const text = `
        Hola ${record.ID_Empleado || 'Empleado'},
        
        Tu solicitud de ${record.TIPO_DE_PERMISO || 'permiso'} ha sido ${status.toLowerCase()} por tu jefe.
        - Fecha Inicio: ${record.FECHA_INICIO}
        - Fecha Fin: ${record.FECHA_FIN}
        
        Gracias por usar el sistema de permisos.
      `;

      await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: record.EMAIL, subject, text }),
      });

      showToast(
        'success',
        status === 'APROBADA' ? 'Solicitud aprobada' : 'Solicitud rechazada',
        'Se notificó al empleado por correo.'
      );
      refreshData();
    } catch (error) {
      console.error('Error al actualizar la solicitud:', error);
      if (mountedRef.current) setAirtableData(prevData);
      showToast(
        'danger',
        'Error',
        'No se pudo actualizar la solicitud. Se revirtió el cambio.'
      );
    } finally {
      if (mountedRef.current) {
        setLoadingRowIds((prev) => {
          const s = new Set(prev);
          s.delete(recordId);
          return s;
        });
      }
    }
  };

  const openCancelConfirm = (record) => {
    setCancelTarget(record);
  };

  const handleCancelConfirmed = async () => {
    if (!cancelTarget) return;
    const record = cancelTarget;

    setCancelLoading(true);
    try {
      // Airtable: DELETE de un registro -> DELETE /v0/{base}/{table}/{recordId}
      const response = await fetchWithRetry(
        `${airtableUrl(TABLES.requests)}/${record.id}`,
        {
          method: 'DELETE',
          headers: AIRTABLE_HEADERS,
        }
      );

      if (!response.ok) {
        throw new Error('No se pudo eliminar la solicitud en Airtable');
      }

      if (mountedRef.current) {
        setPastRequests((prev) => prev.filter((r) => r.id !== record.id));
        setCancelTarget(null);
      }

      showResult(
        'success',
        'Solicitud cancelada',
        'Tu solicitud pendiente fue cancelada y eliminada correctamente.'
      );
      refreshData();
    } catch (error) {
      console.error('Error al cancelar la solicitud:', error);
      showResult('danger', 'Error al cancelar', `No se pudo cancelar la solicitud: ${error.message}`);
      if (mountedRef.current) setCancelTarget(null);
    } finally {
      if (mountedRef.current) setCancelLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const loggedInUser = userData && userData.length > 0 ? userData[0] : null;

    if (!userData || userData.length === 0) {
      showResult('danger', 'Datos no disponibles', 'No se pudo obtener los datos del usuario.');
      return;
    }

    const evaluation = evaluateRequestRules({
      formData,
      userData,
      now: new Date(),
    });
    if (!evaluation.ok) {
      showResult(evaluation.variant, evaluation.title, evaluation.message);
      return;
    }

    formData.permissionType = evaluation.permissionType;
    const { formattedStartDate, formattedEndDate, encounter } = evaluation;
    const { selectedBoss, bossField } = resolveBossField(bosses, formData.boss);
    const requestBody = {
      fields: buildRequestFields({
        formData,
        permissionType: evaluation.permissionType,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        bossField,
      }),
    };

    const sendEmailNotification = async (bossFieldArg, loggedInUserArg, formDataArg) => {
      const subject = 'Nueva solicitud de vacaciones o día económico';
      const text = `
        Hola ${selectedBoss ? selectedBoss.name : 'Jefe'},
        
        Tienes una nueva solicitud de permiso de parte de ${loggedInUserArg.fields.Nombre || 'un empleado'}.
        Detalles de la solicitud:
        - Tipo de Permiso: ${formDataArg.permissionType}
        - Fecha Inicio: ${formDataArg.startDate}
        - Fecha Fin: ${formDataArg.endDate}

        Por favor revisa y aprueba o rechaza la solicitud en el sistema.
      `;

      await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: bossFieldArg, subject, text }),
      });
    };

    setSubmitting(true);

    if (encounter === 'WC') {
      const splitRequests = splitWcDateRanges(formattedStartDate, formattedEndDate).map((range) => ({
        fields: {
          ...requestBody.fields,
          FECHA_INICIO: range.start,
          FECHA_FIN: range.end,
        },
      }));

      try {
        for (const request of splitRequests) {
          await fetchWithRetry(airtableUrl(TABLES.requests), {
            method: 'POST',
            headers: {
              ...AIRTABLE_HEADERS,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          });
        }

        try {
          await sendEmailNotification(bossField, loggedInUser, formData);
          showResult(
            'success',
            'Solicitud enviada',
            'Solicitud dividida y enviada correctamente. Se ha notificado a tu jefe.'
          );
        } catch (error) {
          console.error('Error al enviar el correo:', error);
          showResult(
            'warning',
            'Enviada sin notificación',
            'Solicitud enviada correctamente, pero hubo un error al notificar a tu jefe.'
          );
        } finally {
          refreshData();
        }
      } catch (error) {
        console.error('Error al enviar las solicitudes WC:', error);
        showResult('danger', 'Error al enviar', `No se pudo enviar la solicitud: ${error.message}`);
      } finally {
        if (mountedRef.current) setSubmitting(false);
      }
      return;
    }

    try {
      const response = await fetchWithRetry(airtableUrl(TABLES.requests), {
        method: 'POST',
        headers: {
          ...AIRTABLE_HEADERS,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        throw new Error(
          `Error al enviar la solicitud de vacaciones: ${errorData.error.message}`
        );
      }

      // Reflejar la nueva solicitud de inmediato en la lista (update optimista).
      try {
        const createdRecord = await response.json();
        if (createdRecord && createdRecord.id && mountedRef.current) {
          setPastRequests((prev) => [createdRecord, ...prev]);
        }
      } catch (parseErr) {
        console.warn('No se pudo parsear el registro creado:', parseErr);
      }

      await sendEmailNotification(bossField, loggedInUser, formData);
      showResult(
        'success',
        'Solicitud enviada',
        'Solicitud enviada correctamente. Se ha notificado a tu jefe.'
      );
      refreshData();
    } catch (error) {
      console.error('Error al enviar la solicitud:', error);
      showResult('danger', 'Error al enviar', `No se pudo enviar la solicitud: ${error.message}`);
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const filteredSortedRecords = useMemo(
    () =>
      filterAndSortBossRequests(airtableData, {
        searchQuery: debouncedSearch,
        filterType,
        sortBy,
        sortOrder,
        dateRangeFilter,
      }),
    [airtableData, debouncedSearch, filterType, sortBy, sortOrder, dateRangeFilter]
  );

  // Paginación de la tabla del jefe.
  const bossTotalPages = Math.max(1, Math.ceil(filteredSortedRecords.length / BOSS_PAGE_SIZE));

  // Reinicia a la primera página cuando cambian filtros/búsqueda/orden.
  useEffect(() => {
    setBossPage(1);
  }, [debouncedSearch, filterType, sortBy, sortOrder, dateRangeFilter]);

  // Mantiene la página dentro de rango si la lista se acorta.
  useEffect(() => {
    if (bossPage > bossTotalPages) setBossPage(bossTotalPages);
  }, [bossPage, bossTotalPages]);

  const pagedRecords = useMemo(
    () => pageRecords(filteredSortedRecords, bossPage, BOSS_PAGE_SIZE),
    [filteredSortedRecords, bossPage]
  );

  const bossStatusCounts = useMemo(() => countRequestStatuses(airtableData), [airtableData]);
  const countPending = bossStatusCounts.pending;
  const countApproved = bossStatusCounts.approved;
  const countRejected = bossStatusCounts.rejected;

  const filterChips = [];
  if (debouncedSearch) {
    filterChips.push({
      key: 'search',
      label: `Búsqueda: ${debouncedSearch}`,
      clear: () => setSearchQuery(''),
    });
  }
  if (filterType !== 'todos') {
    const labels = { PENDIENTE: 'Pendientes', APROBADA: 'Aprobadas', RECHAZADA: 'Rechazadas' };
    filterChips.push({
      key: 'status',
      label: `Estado: ${labels[filterType] || filterType}`,
      clear: () => setFilterType('todos'),
    });
  }
  if (dateRangeFilter.startDate) {
    filterChips.push({
      key: 'date-start',
      label: `Desde: ${formatDate(dateRangeFilter.startDate)}`,
      clear: () => setDateRangeFilter((d) => ({ ...d, startDate: '' })),
    });
  }
  if (dateRangeFilter.endDate) {
    filterChips.push({
      key: 'date-end',
      label: `Hasta: ${formatDate(dateRangeFilter.endDate)}`,
      clear: () => setDateRangeFilter((d) => ({ ...d, endDate: '' })),
    });
  }

  const hasAnyFilter = filterChips.length > 0;

  const clearAllFilters = () => {
    setSearchQuery('');
    setFilterType('todos');
    setSortBy('jerarquico');
    setSortOrder('asc');
    setDateRangeFilter({ startDate: '', endDate: '' });
  };

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortOrder('asc');
    }
  };

  const sortArrow = (col) => {
    if (sortBy !== col) return null;
    return <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} sort-arrow`} />;
  };

  const getTypeBadgeClass = (tipo) => {
    if (tipo === 'vacaciones') return 'vacaciones';
    if (tipo === 'dia_economico') return 'dia-economico';
    return 'other';
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'APROBADA') return 'approved';
    if (status === 'RECHAZADA') return 'rejected';
    return 'pending';
  };

  const getRowStatusClass = (status) => {
    if (status === 'APROBADA') return 'status-approved';
    if (status === 'RECHAZADA') return 'status-rejected';
    return 'status-pending';
  };

  const statusLabel = (s) =>
    s === 'APROBADA' ? 'Aprobada' : s === 'RECHAZADA' ? 'Rechazada' : 'Pendiente';

  const statusIcon = (s) =>
    s === 'APROBADA'
      ? 'bi-check-circle-fill'
      : s === 'RECHAZADA'
      ? 'bi-x-circle-fill'
      : 'bi-clock-fill';

  const typeLabel = (t) =>
    t === 'vacaciones' ? 'Vacaciones' : t === 'dia_economico' ? 'Día Económico' : t || 'N/A';

  const typeIcon = (t) =>
    t === 'vacaciones' ? 'bi-airplane-fill' : t === 'dia_economico' ? 'bi-calendar-event-fill' : 'bi-question-circle';

  const isVigenciaExpired = (finStr) => {
    if (!finStr || finStr === 'No aplica') return false;
    return parseVigenciaDate(finStr, true) < new Date();
  };

  const displayName =
    role === 'employee' && userData?.[0]?.fields?.Nombre
      ? userData[0].fields.Nombre
      : userEmail || 'Usuario';
  const initials = getInitials(displayName);

  const remainingTotals = useMemo(() => sumRemainingBalances(userData || []), [userData]);
  const totalVac = remainingTotals.vacationDaysRemaining;
  const totalEco = remainingTotals.economicDaysRemaining;

  const pastFiltered = useMemo(
    () => filterPastRequests(pastRequests, pastFilter),
    [pastRequests, pastFilter]
  );

  const pastCounts = useMemo(() => {
    const counted = countRequestStatuses(pastRequests);
    return { total: counted.total, p: counted.pending, a: counted.approved, r: counted.rejected };
  }, [pastRequests]);

  const previewRange = useMemo(() => {
    if (!formData.startDate || !formData.endDate) return null;
    const days = computeDayRange(formData.startDate, formData.endDate);
    if (!days) return null;
    const startLabel = formatDate(formData.startDate);
    const endLabel = formatDate(formData.endDate);
    return { startLabel, endLabel, days };
  }, [formData.startDate, formData.endDate]);

  if (!roleLoaded) {
    return <FullScreenSpinner label="Cargando dashboard..." />;
  }

  const renderNavbar = () => (
    <nav className="bv-navbar">
      <div className="bv-navbar-inner">
        <div className="bv-navbar-brand">
          <img
            src="https://cdn.shopify.com/s/files/1/1437/8626/files/Homepage_0004_Falcon.png?v=1614320785"
            alt="Birdman"
            className="bv-navbar-logo"
          />
          <span>Birdman Vacaciones</span>
        </div>
        <div className="bv-navbar-user">
          <div className="bv-user-info">
            <span className="bv-user-name">{displayName}</span>
            <span className={`bv-user-role ${role === 'boss' ? 'boss' : ''}`}>
              {role === 'boss' ? 'Jefe(a)' : 'Empleado(a)'}
            </span>
          </div>
          <div className="bv-navbar-avatar" aria-hidden="true">
            {initials}
          </div>
          <button
            type="button"
            className="bv-logout-btn"
            onClick={() => setLogoutConfirm(true)}
            aria-label="Cerrar sesión"
          >
            <i className="bi bi-box-arrow-right" />
            <span className="d-none d-sm-inline">Cerrar sesión</span>
          </button>
        </div>
      </div>
    </nav>
  );

  const renderEmployeeSection = () => {
    if (!dataLoaded) {
      return (
        <>
          <div className="bv-loading-hint">
            <i className="bi bi-cloud-arrow-down" />
            Consultando tus datos en Airtable... esto puede tardar unos segundos.
          </div>
          <SkeletonCard />
          <SkeletonForm />
        </>
      );
    }

    return (
      <>
        <div className="bv-saldo-banner">
          <div className="bv-saldo-item">
            <div className="bv-saldo-icon vac">
              <i className="bi bi-airplane-fill" />
            </div>
            <div className="bv-saldo-content">
              <span className="bv-saldo-label">Vacaciones disponibles</span>
              <span className="bv-saldo-value">
                {totalVac}
                <small>días</small>
              </span>
            </div>
          </div>
          <div className="bv-saldo-item">
            <div className="bv-saldo-icon eco">
              <i className="bi bi-calendar-event-fill" />
            </div>
            <div className="bv-saldo-content">
              <span className="bv-saldo-label">Días económicos disponibles</span>
              <span className="bv-saldo-value">
                {totalEco}
                <small>días</small>
              </span>
            </div>
          </div>
        </div>

        <h2 className="bv-section-title">
          <i className="bi bi-calendar-heart-fill" />
          Mis Ciclos
        </h2>
        {userData && userData.length > 0 ? (
          <div className="bv-cycles">
            {userData.map((record, index) => {
              const availableVac = Number(record.fields.VACACIONES) || 0;
              const availableEco = Number(record.fields.DIAS_ECONOMICOS) || 0;

              const vacationData = {
                labels: ['Usadas', 'Restantes'],
                datasets: [
                  {
                    data: [record.vacationDaysUsed || 0, record.vacationDaysRemaining || 0],
                    backgroundColor: ['#e5e7eb', '#0d6efd'],
                    hoverBackgroundColor: ['#d1d5db', '#0a58ca'],
                    borderWidth: 0,
                  },
                ],
              };

              const economicData = {
                labels: ['Usados', 'Restantes'],
                datasets: [
                  {
                    data: [record.economicDaysUsed || 0, record.economicDaysRemaining || 0],
                    backgroundColor: ['#e5e7eb', '#d97706'],
                    hoverBackgroundColor: ['#d1d5db', '#b45309'],
                    borderWidth: 0,
                  },
                ],
              };

              const chartOptions = {
                cutout: '68%',
                plugins: {
                  legend: { display: false },
                  tooltip: { enabled: true },
                },
                maintainAspectRatio: false,
              };

              const vacExpired = record._isVacExpired || isVigenciaExpired(record.fields.VAC_Fin_Vigencia);
              const deExpired = record._isDeExpired || isVigenciaExpired(record.fields.DE_Fin_Vigencia);
              const bothExpired = vacExpired && deExpired;
              const anyActive = !vacExpired || !deExpired;

              return (
                <div key={record.id || index} className="bv-cycle-card">
                  <div className="bv-cycle-header">
                    <h3 className="bv-cycle-title">
                      <i className="bi bi-calendar-range-fill" />
                      Ciclo de Vacaciones
                    </h3>
                    <span className={`bv-badge ${bothExpired ? 'expired' : anyActive ? 'active' : 'na'}`}>
                      <i className={`bi ${bothExpired ? 'bi-calendar-x' : 'bi-check-circle-fill'}`} />
                      {bothExpired ? 'Vencido' : 'Activo'}
                    </span>
                  </div>
                  <div className="bv-cycle-body">
                    <div className="bv-charts-row">
                      <div className="bv-chart-block">
                        <div className="bv-chart">
                          <Doughnut data={vacationData} options={chartOptions} />
                          <div className="bv-chart-center">
                            <div className="bv-chart-number">
                              {record.vacationDaysUsed || 0}
                              <small>/{availableVac}</small>
                            </div>
                            <div className="bv-chart-sub">usados</div>
                          </div>
                        </div>
                        <div className="bv-chart-caption">
                          <i className="bi bi-airplane-fill" />
                          Vacaciones
                        </div>
                      </div>
                      <div className="bv-chart-block">
                        <div className="bv-chart">
                          <Doughnut data={economicData} options={chartOptions} />
                          <div className="bv-chart-center">
                            <div className="bv-chart-number">
                              {record.economicDaysUsed || 0}
                              <small>/{availableEco}</small>
                            </div>
                            <div className="bv-chart-sub">usados</div>
                          </div>
                        </div>
                        <div className="bv-chart-caption">
                          <i className="bi bi-calendar-event-fill eco-icon" />
                          Días Económicos
                        </div>
                      </div>
                    </div>

                    <div className="bv-info-chips">
                      <span className="bv-chip">
                        <i className="bi bi-person-badge" />
                        <strong>ID:</strong>
                        {record.fields.ID_Empleado || 'N/A'}
                      </span>
                      <span className="bv-chip">
                        <i className="bi bi-calendar-plus" />
                        <strong>Ingreso:</strong>
                        {record.fields.Fecha_Ingreso
                          ? formatDate(record.fields.Fecha_Ingreso)
                          : 'N/A'}
                      </span>
                      <span className="bv-chip">
                        <i className="bi bi-hourglass-split" />
                        <strong>Antigüedad:</strong>
                        {record.fields.Antigüedad || 'N/A'} años
                      </span>
                      <span className="bv-chip">
                        <i className="bi bi-airplane" />
                        <strong>Vig. Vac:</strong>
                        {record.fields.VAC_Inicio_Vigencia || 'N/A'} - {record.fields.VAC_Fin_Vigencia || 'N/A'}
                      </span>
                      <span className="bv-chip">
                        <i className="bi bi-calendar-event" />
                        <strong>Vig. DE:</strong>
                        {record.fields.DE_Inicio_Vigencia || 'N/A'} - {record.fields.DE_Fin_Vigencia || 'N/A'}
                      </span>
                    </div>
                    {record._invalidRequestsForPeriod?.length > 0 && (
                      <div className="alert alert-warning mt-3 mb-0" role="alert">
                        <strong>Solicitud aprobada omitida por fechas invalidas.</strong>{' '}
                        IDs afectados:{' '}
                        {record._invalidRequestsForPeriod
                          .map((item) => `${item.id} (${item.reason})`)
                          .join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bv-empty">
            <i className="bi bi-inbox" />
            <h4>Sin registros</h4>
            <p>No se encontraron datos para el usuario.</p>
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          <div className="bv-form-card">
            <h3 className="bv-form-title">
              <i className="bi bi-send-fill" />
              Solicitar Permiso
            </h3>
            <p className="bv-form-subtitle">
              Tus datos se toman automáticamente de tu perfil. Completa las fechas y tipo de permiso.
            </p>

            <div className="bv-form-grid">
              <Form.Group>
                <Form.Label className="bv-form-label">Número de Empleado</Form.Label>
                <Form.Control
                  type="text"
                  name="employeeNumber"
                  value={formData.employeeNumber}
                  disabled
                  readOnly
                />
              </Form.Group>
              <Form.Group>
                <Form.Label className="bv-form-label">Correo Electrónico</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  disabled
                  readOnly
                />
              </Form.Group>
              <Form.Group>
                <Form.Label className="bv-form-label">Fecha Inicio</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  disabled={submitting}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label className="bv-form-label">Fecha Fin</Form.Label>
                <Form.Control
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  disabled={submitting}
                />
              </Form.Group>
            </div>

            <div className="bv-form-full mb-3">
              <Form.Label className="bv-form-label">Tipo de Permiso</Form.Label>
              <div className="bv-form-radio-group">
                <label className="bv-form-radio">
                  <input
                    type="radio"
                    name="permissionType"
                    value="vacaciones"
                    checked={formData.permissionType === 'vacaciones'}
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                  <span className="bv-form-radio-label">
                    <i className="bi bi-airplane-fill" />
                    Vacaciones
                  </span>
                </label>
                <label className="bv-form-radio">
                  <input
                    type="radio"
                    name="permissionType"
                    value="dia_economico"
                    checked={formData.permissionType === 'dia_economico'}
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                  <span className="bv-form-radio-label">
                    <i className="bi bi-calendar-event-fill" />
                    Día Económico
                  </span>
                </label>
              </div>
            </div>

            <div className="bv-form-full mb-3">
              <Form.Label className="bv-form-label">Jefe(a) Directo(a)</Form.Label>
              <Form.Select
                name="boss"
                value={formData.boss}
                onChange={handleInputChange}
                disabled={submitting}
              >
                <option value="">Selecciona a tu jefe</option>
                {bosses.map((boss, index) => (
                  <option key={index} value={boss.email}>
                    {boss.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            {previewRange && (
              <div className="bv-form-preview">
                <i className="bi bi-info-circle-fill" />
                <span>
                  Periodo solicitado: <strong>{previewRange.startLabel}</strong> al{' '}
                  <strong>{previewRange.endLabel}</strong> ({previewRange.days} día
                  {previewRange.days !== 1 ? 's' : ''})
                </span>
              </div>
            )}

            <div className="bv-form-actions">
              <button type="submit" className="bv-btn-primary" disabled={submitting}>
                {submitting ? (
                  <>
                    <span
                      className="bv-spinner"
                      style={{ width: 14, height: 14, borderWidth: 2 }}
                    />
                    Enviando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-send-fill" />
                    Enviar Solicitud
                  </>
                )}
              </button>
            </div>
          </div>
        </Form>

        <div className="bv-past-card">
          <div className="bv-past-header">
            <h3 className="bv-past-title">
              <i className="bi bi-clock-history" />
              Mis Solicitudes Pasadas
            </h3>
            <div className="bv-tabs" role="tablist">
              <button
                type="button"
                className={`bv-tab ${pastFilter === 'todas' ? 'active' : ''}`}
                onClick={() => setPastFilter('todas')}
              >
                Todas<span className="count">({pastCounts.total})</span>
              </button>
              <button
                type="button"
                className={`bv-tab ${pastFilter === 'pendientes' ? 'active' : ''}`}
                onClick={() => setPastFilter('pendientes')}
              >
                Pendientes<span className="count">({pastCounts.p})</span>
              </button>
              <button
                type="button"
                className={`bv-tab ${pastFilter === 'aprobadas' ? 'active' : ''}`}
                onClick={() => setPastFilter('aprobadas')}
              >
                Aprobadas<span className="count">({pastCounts.a})</span>
              </button>
              <button
                type="button"
                className={`bv-tab ${pastFilter === 'rechazadas' ? 'active' : ''}`}
                onClick={() => setPastFilter('rechazadas')}
              >
                Rechazadas<span className="count">({pastCounts.r})</span>
              </button>
            </div>
          </div>

          {pastFiltered.length > 0 ? (
            <>
              <div className="bv-past-table-wrap">
                <table className="bv-past-table">
                  <thead>
                    <tr>
                      <th>Fecha Inicio</th>
                      <th>Fecha Fin</th>
                      <th>Tipo</th>
                      <th>Jefe</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pastFiltered.map((record, index) => {
                      const status = record.fields?.APROBACION || 'PENDIENTE';
                      const tipo = (record.fields?.TIPO_DE_PERMISO || '').toLowerCase();
                      return (
                        <tr
                          key={record.id || index}
                          className={getRowStatusClass(status)}
                        >
                          <td>
                            {record.fields?.FECHA_INICIO
                              ? formatDate(record.fields.FECHA_INICIO)
                              : 'N/A'}
                          </td>
                          <td>
                            {record.fields?.FECHA_FIN
                              ? formatDate(record.fields.FECHA_FIN)
                              : 'N/A'}
                          </td>
                          <td>
                            <span className={`bv-type-badge ${getTypeBadgeClass(tipo)}`}>
                              <i className={`bi ${typeIcon(tipo)}`} />
                              {typeLabel(tipo)}
                            </span>
                          </td>
                          <td>
                            {record.fields?.JEFE
                              ? typeof record.fields.JEFE === 'object'
                                ? record.fields.JEFE.name
                                : record.fields.JEFE
                              : 'N/A'}
                          </td>
                          <td>
                            <span className={`bv-status-badge ${getStatusBadgeClass(status)}`}>
                              <i className={`bi ${statusIcon(status)}`} />
                              {statusLabel(status)}
                            </span>
                          </td>
                          <td>
                            {canCancelRequest(status) ? (
                              <button
                                type="button"
                                className="bv-cancel-btn"
                                onClick={() => openCancelConfirm(record)}
                                aria-label="Cancelar solicitud"
                              >
                                <i className="bi bi-trash3" />
                                Cancelar
                              </button>
                            ) : (
                              <span className="bv-cancel-na">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="bv-past-mobile">
                {pastFiltered.map((record, index) => {
                  const status = record.fields?.APROBACION || 'PENDIENTE';
                  const tipo = (record.fields?.TIPO_DE_PERMISO || '').toLowerCase();
                  return (
                    <div
                      key={record.id || index}
                      className={`bv-mobile-card ${getRowStatusClass(status)}`}
                    >
                      <div className="bv-mobile-card-header">
                        <span className={`bv-type-badge ${getTypeBadgeClass(tipo)}`}>
                          <i className={`bi ${typeIcon(tipo)}`} />
                          {typeLabel(tipo)}
                        </span>
                        <span className={`bv-status-badge ${getStatusBadgeClass(status)}`}>
                          <i className={`bi ${statusIcon(status)}`} />
                          {statusLabel(status)}
                        </span>
                      </div>
                      <div className="bv-mobile-card-row">
                        <strong>Fechas</strong>
                        <span>
                          {record.fields?.FECHA_INICIO
                            ? formatDate(record.fields.FECHA_INICIO)
                            : 'N/A'}{' '}
                          –{' '}
                          {record.fields?.FECHA_FIN
                            ? formatDate(record.fields.FECHA_FIN)
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="bv-mobile-card-row">
                        <strong>Jefe</strong>
                        <span>
                          {record.fields?.JEFE
                            ? typeof record.fields.JEFE === 'object'
                              ? record.fields.JEFE.name
                              : record.fields.JEFE
                            : 'N/A'}
                        </span>
                      </div>
                      {status === 'PENDIENTE' && (
                        <button
                          type="button"
                          className="bv-cancel-btn bv-cancel-btn-block"
                          onClick={() => openCancelConfirm(record)}
                          aria-label="Cancelar solicitud"
                        >
                          <i className="bi bi-trash3" />
                          Cancelar solicitud
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="bv-empty" style={{ margin: '1rem', border: 'none' }}>
              <i className="bi bi-inbox" />
              <h4>No hay solicitudes</h4>
              <p>
                {pastFilter === 'todas'
                  ? 'No tienes solicitudes pasadas registradas.'
                  : 'No hay solicitudes con este estado.'}
              </p>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderBossSection = () => (
    <>
      <div className="bv-admin-header">
        <h2>Panel de Administración</h2>
        <p>Gestiona las solicitudes de vacaciones y días económicos de tu equipo</p>
      </div>

      {!dataLoaded ? (
        <>
          <div className="bv-loading-hint">
            <i className="bi bi-cloud-arrow-down" />
            Consultando solicitudes de tu equipo en Airtable... esto puede tardar unos segundos.
          </div>
          <SkeletonStats count={3} />
          <SkeletonTable rows={6} cols={7} />
        </>
      ) : (
        <>
          <div className="bv-stats-grid">
            <div className="bv-stat-card pending">
              <div className="bv-stat-icon">
                <i className="bi bi-clock-fill" />
              </div>
              <div className="bv-stat-label">Pendientes</div>
              <div className="bv-stat-value">{countPending}</div>
            </div>
            <div className="bv-stat-card approved">
              <div className="bv-stat-icon">
                <i className="bi bi-check-circle-fill" />
              </div>
              <div className="bv-stat-label">Aprobadas</div>
              <div className="bv-stat-value">{countApproved}</div>
            </div>
            <div className="bv-stat-card rejected">
              <div className="bv-stat-icon">
                <i className="bi bi-x-circle-fill" />
              </div>
              <div className="bv-stat-label">Rechazadas</div>
              <div className="bv-stat-value">{countRejected}</div>
            </div>
          </div>

          <div className="bv-filters-card">
            <div className="bv-filters-header">
              <h4 className="bv-filters-title">
                <i className="bi bi-funnel-fill" />
                Filtros y Ordenamiento
              </h4>
              <button
                type="button"
                className="bv-btn-ghost"
                onClick={() => setExpandedFilters(!expandedFilters)}
              >
                <i className={`bi ${expandedFilters ? 'bi-chevron-up' : 'bi-sliders'}`} />
                {expandedFilters ? 'Ocultar filtros avanzados' : 'Filtros avanzados'}
              </button>
            </div>

            <div className="bv-filters-grid">
              <Form.Group>
                <Form.Label className="bv-form-label">Buscar</Form.Label>
                <div className="bv-search-input">
                  <i className="bi bi-search bv-search-icon" />
                  <Form.Control
                    type="text"
                    placeholder="Buscar por empleado, correo o tipo..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="bv-search-clear"
                      onClick={() => setSearchQuery('')}
                      aria-label="Limpiar búsqueda"
                    >
                      <i className="bi bi-x-circle-fill" />
                    </button>
                  )}
                </div>
              </Form.Group>
              <Form.Group>
                <Form.Label className="bv-form-label">Estado de solicitud</Form.Label>
                <Form.Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="todos">Todos los estados</option>
                  <option value="PENDIENTE">Pendientes</option>
                  <option value="APROBADA">Aprobadas</option>
                  <option value="RECHAZADA">Rechazadas</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className={`bv-filters-advanced ${expandedFilters ? 'expanded' : ''}`}>
              <div className="bv-filters-grid">
                <Form.Group>
                  <Form.Label className="bv-form-label">Ordenar por</Form.Label>
                  <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="jerarquico">Jerárquico (predeterminado)</option>
                    <option value="fecha_inicio">Fecha de inicio</option>
                    <option value="fecha_fin">Fecha de fin</option>
                    <option value="id_empleado">Número de empleado</option>
                    <option value="tipo_permiso">Tipo de permiso</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label className="bv-form-label">Orden</Form.Label>
                  <Form.Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                  >
                    <option value="asc">Ascendente</option>
                    <option value="desc">Descendente</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label className="bv-form-label">Desde</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateRangeFilter.startDate}
                    onChange={(e) =>
                      setDateRangeFilter({ ...dateRangeFilter, startDate: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label className="bv-form-label">Hasta</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateRangeFilter.endDate}
                    onChange={(e) =>
                      setDateRangeFilter({ ...dateRangeFilter, endDate: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
              <div className="bv-filters-chips">
                {filterChips.map((chip) => (
                  <span key={chip.key} className="bv-filter-chip">
                    {chip.label}
                    <button type="button" onClick={chip.clear} aria-label="Quitar filtro">
                      <i className="bi bi-x" />
                    </button>
                  </span>
                ))}
                {hasAnyFilter && (
                  <button type="button" className="bv-btn-ghost" onClick={clearAllFilters}>
                    <i className="bi bi-x-circle" />
                    Limpiar todo
                  </button>
                )}
              </div>
              <span className="bv-results-counter">
                <i className="bi bi-list-ul" />
                <strong>{filteredSortedRecords.length}</strong>
                de {airtableData.length} solicitudes
              </span>
            </div>
          </div>

          {filteredSortedRecords.length > 0 ? (
            <>
              <div className="bv-table-wrap">
                <table className="bv-table">
                  <thead>
                    <tr>
                      <th
                        className={`sortable ${sortBy === 'id_empleado' ? 'active' : ''}`}
                        onClick={() => handleSort('id_empleado')}
                      >
                        Empleado {sortArrow('id_empleado')}
                      </th>
                      <th
                        className={`sortable ${sortBy === 'fecha_inicio' ? 'active' : ''}`}
                        onClick={() => handleSort('fecha_inicio')}
                      >
                        Periodo {sortArrow('fecha_inicio')}
                      </th>
                      <th
                        className={`sortable ${sortBy === 'tipo_permiso' ? 'active' : ''}`}
                        onClick={() => handleSort('tipo_permiso')}
                      >
                        Tipo {sortArrow('tipo_permiso')}
                      </th>
                      <th>Jefe</th>
                      <th>Estado</th>
                      <th style={{ textAlign: 'right' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRecords.map((record, index) => {
                      const status = record.fields.APROBACION || 'PENDIENTE';
                      const tipo = (record.fields.TIPO_DE_PERMISO || '').toLowerCase();
                      const loading = loadingRowIds.has(record.id);
                      const days = computeDayRange(
                        record.fields.FECHA_INICIO,
                        record.fields.FECHA_FIN
                      );
                      return (
                        <tr
                          key={record.id || index}
                          className={getRowStatusClass(status)}
                        >
                          <td>
                            <div className="bv-employee-cell">
                              <div className="bv-avatar">
                                <span className="bv-avatar-id">
                                  {record.fields?.ID_Empleado
                                    ? String(record.fields.ID_Empleado).slice(-3)
                                    : '?'}
                                </span>
                              </div>
                              <div className="bv-employee-meta">
                                <span className="bv-employee-id">
                                  #{record.fields?.ID_Empleado || 'N/A'}
                                </span>
                                <span className="bv-employee-email" title={record.fields?.EMAIL}>
                                  {record.fields?.EMAIL || 'N/A'}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="bv-date-cell">
                              <span className="bv-date-main">
                                {record.fields?.FECHA_INICIO
                                  ? formatDate(record.fields.FECHA_INICIO)
                                  : 'N/A'}{' '}
                                –{' '}
                                {record.fields?.FECHA_FIN
                                  ? formatDate(record.fields.FECHA_FIN)
                                  : 'N/A'}
                              </span>
                              {days && (
                                <span className="bv-date-sub">
                                  {days} día{days !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className={`bv-type-badge ${getTypeBadgeClass(tipo)}`}>
                              <i className={`bi ${typeIcon(tipo)}`} />
                              {typeLabel(tipo)}
                            </span>
                          </td>
                          <td>
                            {record.fields.JEFE
                              ? typeof record.fields.JEFE === 'object'
                                ? record.fields.JEFE.name
                                : record.fields.JEFE
                              : 'N/A'}
                          </td>
                          <td>
                            <span className={`bv-status-badge ${getStatusBadgeClass(status)}`}>
                              <i className={`bi ${statusIcon(status)}`} />
                              {statusLabel(status)}
                            </span>
                          </td>
                          <td>
                            {canCancelRequest(status) ? (
                              <div className="bv-actions-cell">
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>Aprobar solicitud</Tooltip>}
                                >
                                  <button
                                    type="button"
                                    className="bv-icon-btn approve"
                                    onClick={() =>
                                      openApprovalConfirm(record.id, 'APROBADA', record)
                                    }
                                    disabled={loading}
                                    aria-label="Aprobar"
                                  >
                                    {loading ? (
                                      <span
                                        className="bv-spinner"
                                        style={{ width: 14, height: 14, borderWidth: 2 }}
                                      />
                                    ) : (
                                      <i className="bi bi-check-lg" />
                                    )}
                                  </button>
                                </OverlayTrigger>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>Rechazar solicitud</Tooltip>}
                                >
                                  <button
                                    type="button"
                                    className="bv-icon-btn reject"
                                    onClick={() =>
                                      openApprovalConfirm(record.id, 'RECHAZADA', record)
                                    }
                                    disabled={loading}
                                    aria-label="Rechazar"
                                  >
                                    {loading ? (
                                      <span
                                        className="bv-spinner"
                                        style={{ width: 14, height: 14, borderWidth: 2 }}
                                      />
                                    ) : (
                                      <i className="bi bi-x-lg" />
                                    )}
                                  </button>
                                </OverlayTrigger>
                              </div>
                            ) : (
                              <div className="bv-actions-cell">
                                <span className="bv-actions-pending-note">Ya procesada</span>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="bv-mobile-cards">
                {pagedRecords.map((record, index) => {
                  const status = record.fields.APROBACION || 'PENDIENTE';
                  const tipo = (record.fields.TIPO_DE_PERMISO || '').toLowerCase();
                  const loading = loadingRowIds.has(record.id);
                  const days = computeDayRange(
                    record.fields.FECHA_INICIO,
                    record.fields.FECHA_FIN
                  );
                  return (
                    <div
                      key={record.id || index}
                      className={`bv-mobile-card ${getRowStatusClass(status)}`}
                    >
                      <div className="bv-mobile-card-header">
                        <div className="bv-employee-cell">
                          <div className="bv-avatar">
                            <span className="bv-avatar-id">
                              {record.fields?.ID_Empleado
                                ? String(record.fields.ID_Empleado).slice(-3)
                                : '?'}
                            </span>
                          </div>
                          <div className="bv-employee-meta">
                            <span className="bv-employee-id">
                              #{record.fields?.ID_Empleado || 'N/A'}
                            </span>
                            <span className="bv-employee-email">
                              {record.fields?.EMAIL || 'N/A'}
                            </span>
                          </div>
                        </div>
                        <span className={`bv-status-badge ${getStatusBadgeClass(status)}`}>
                          <i className={`bi ${statusIcon(status)}`} />
                          {statusLabel(status)}
                        </span>
                      </div>
                      <div className="bv-mobile-card-row">
                        <strong>Periodo</strong>
                        <span>
                          {record.fields?.FECHA_INICIO
                            ? formatDate(record.fields.FECHA_INICIO)
                            : 'N/A'}{' '}
                          –{' '}
                          {record.fields?.FECHA_FIN
                            ? formatDate(record.fields.FECHA_FIN)
                            : 'N/A'}
                          {days && ` · ${days} día${days !== 1 ? 's' : ''}`}
                        </span>
                      </div>
                      <div className="bv-mobile-card-row">
                        <strong>Tipo</strong>
                        <span>
                          <span className={`bv-type-badge ${getTypeBadgeClass(tipo)}`}>
                            <i className={`bi ${typeIcon(tipo)}`} />
                            {typeLabel(tipo)}
                          </span>
                        </span>
                      </div>
                      <div className="bv-mobile-card-row">
                        <strong>Jefe</strong>
                        <span>
                          {record.fields.JEFE
                            ? typeof record.fields.JEFE === 'object'
                              ? record.fields.JEFE.name
                              : record.fields.JEFE
                            : 'N/A'}
                        </span>
                      </div>
                      {status === 'PENDIENTE' && (
                        <div className="bv-mobile-card-actions">
                          <button
                            type="button"
                            className="bv-icon-btn approve"
                            onClick={() => openApprovalConfirm(record.id, 'APROBADA', record)}
                            disabled={loading}
                          >
                            <i className="bi bi-check-lg" />
                            <span>Aprobar</span>
                          </button>
                          <button
                            type="button"
                            className="bv-icon-btn reject"
                            onClick={() => openApprovalConfirm(record.id, 'RECHAZADA', record)}
                            disabled={loading}
                          >
                            <i className="bi bi-x-lg" />
                            <span>Rechazar</span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {bossTotalPages > 1 && (
                <div className="bv-pagination">
                  <button
                    type="button"
                    className="bv-page-btn"
                    onClick={() => setBossPage((p) => Math.max(1, p - 1))}
                    disabled={bossPage <= 1}
                    aria-label="Página anterior"
                  >
                    <i className="bi bi-chevron-left" />
                    Anterior
                  </button>
                  <span className="bv-page-info">
                    Página {bossPage} de {bossTotalPages}
                  </span>
                  <button
                    type="button"
                    className="bv-page-btn"
                    onClick={() => setBossPage((p) => Math.min(bossTotalPages, p + 1))}
                    disabled={bossPage >= bossTotalPages}
                    aria-label="Página siguiente"
                  >
                    Siguiente
                    <i className="bi bi-chevron-right" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="bv-empty">
              <i className="bi bi-funnel" />
              <h4>Sin resultados</h4>
              <p>
                {airtableData.length === 0
                  ? 'No hay solicitudes disponibles para tu equipo.'
                  : 'Ningún registro coincide con los filtros actuales. Intenta limpiar filtros.'}
              </p>
            </div>
          )}
        </>
      )}
    </>
  );

  console.log('Airtable Data in Render:', airtableData);

  return (
    <div className="bv-dashboard">
      {renderNavbar()}
      <div className="bv-dashboard-body">
        {role === 'employee' ? (
          <div className="user-section">{renderEmployeeSection()}</div>
        ) : role === 'boss' ? (
          <div className="admin-section">{renderBossSection()}</div>
        ) : (
          <div className="bv-empty">
            <i className="bi bi-person-x" />
            <h4>Rol no reconocido</h4>
            <p>Por favor vuelve a iniciar sesión.</p>
          </div>
        )}
      </div>

      <ConfirmDialog
        show={!!confirmAction}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmText={confirmAction?.confirmText || 'Confirmar'}
        cancelText="Cancelar"
        variant={confirmAction?.variant || 'primary'}
        icon={confirmAction?.icon || 'bi-question-circle-fill'}
        loading={confirmLoading}
        onConfirm={handleApprovalConfirmed}
        onCancel={() => setConfirmAction(null)}
      />

      <ConfirmDialog
        show={!!cancelTarget}
        title="Cancelar solicitud"
        message={
          cancelTarget
            ? `¿Seguro que deseas cancelar tu solicitud de ${
                (cancelTarget.fields?.TIPO_DE_PERMISO || 'permiso')
              } del ${formatDate(cancelTarget.fields?.FECHA_INICIO)} al ${formatDate(
                cancelTarget.fields?.FECHA_FIN
              )}? Esta acción eliminará la solicitud de forma permanente y no se puede deshacer.`
            : ''
        }
        confirmText="Sí, cancelar"
        cancelText="No, mantener"
        variant="danger"
        icon="bi-trash3-fill"
        loading={cancelLoading}
        onConfirm={handleCancelConfirmed}
        onCancel={() => setCancelTarget(null)}
      />

      <ConfirmDialog
        show={logoutConfirm}
        title="Cerrar sesión"
        message="¿Seguro que deseas cerrar sesión?"
        confirmText="Sí, cerrar sesión"
        cancelText="Cancelar"
        variant="primary"
        icon="bi-box-arrow-right"
        onConfirm={() => {
          setLogoutConfirm(false);
          handleLogout();
        }}
        onCancel={() => setLogoutConfirm(false)}
      />

      <ResultDialog
        show={!!resultModal}
        variant={resultModal?.variant || 'info'}
        title={resultModal?.title || ''}
        message={resultModal?.message || ''}
        onClose={() => setResultModal(null)}
      />
    </div>
  );
};

export default Dashboard;
